// src/ui/notifications/bell.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const LS_KEY = "ll.notifications";

function load() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function save(arr) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(arr.slice(0, 200))); } catch {}
}

// API simples p/ o resto da app usar (e para testes)
export const inbox = {
  push(n) {
    const item = {
      id: crypto.randomUUID(),
      level: n.level || "info", // 'info' | 'success' | 'warning' | 'error'
      title: n.title || "",
      message: n.message || "",
      ctaUrl: n.ctaUrl || "",
      entity: n.entity || null,  // { kind:'leilao', id:number }
      read: false,
      createdAt: n.createdAt || new Date().toISOString(),
    };
    const arr = [item, ...load()];
    save(arr);
    window.dispatchEvent(new CustomEvent("ll:inbox:update"));
  },
  markAllRead() {
    const arr = load().map(n => ({ ...n, read: true }));
    save(arr);
    window.dispatchEvent(new CustomEvent("ll:inbox:update"));
  },
  markRead(id) {
    const arr = load().map(n => (n.id === id ? { ...n, read: true } : n));
    save(arr);
    window.dispatchEvent(new CustomEvent("ll:inbox:update"));
  },
  list() { return load(); },
};

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(load());
  const unread = useMemo(() => items.filter(i => !i.read).length, [items]);
  const ref = useRef(null);

  useEffect(() => {
    function sync() { setItems(load()); }
    window.addEventListener("ll:inbox:update", sync);
    return () => window.removeEventListener("ll:inbox:update", sync);
  }, []);

  // fechar ao clicar fora
  useEffect(() => {
    function onDoc(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative h-10 w-10 rounded-full bg-white ring-1 ring-neutral-200 grid place-items-center hover:bg-neutral-50"
        aria-label="Notificações"
      >
        {/* ícone simples */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M12 22a2 2 0 0 0 2-2H10a2 2 0 0 0 2 2Zm6-6V11a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2Z" fill="#0b0a08"/>
        </svg>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full px-1.5 py-[2px]">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden z-[1100]">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <strong className="text-sm">Notificações</strong>
            <button
              onClick={() => inbox.markAllRead()}
              className="text-xs text-blue-600 hover:underline"
            >
              Marcar tudo como lido
            </button>
          </div>

          <div className="max-h-96 overflow-auto divide-y">
            {items.length === 0 && (
              <div className="p-4 text-sm text-neutral-500">Sem novidades por aqui.</div>
            )}
            {items.map((n) => (
              <a
                key={n.id}
                href={n.ctaUrl || "#"}
                onClick={() => inbox.markRead(n.id)}
                className={`block p-3 hover:bg-neutral-50 ${n.read ? "opacity-70" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {/* marcador por nível */}
                  <span className={
                    "mt-1 inline-block h-2 w-2 rounded-full " + (
                      n.level === "success" ? "bg-green-500" :
                      n.level === "warning" ? "bg-amber-500" :
                      n.level === "error" ? "bg-red-500" : "bg-blue-500"
                    )
                  } />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{n.title || "Atualização"}</div>
                    {n.message && <div className="text-xs text-neutral-600 truncate">{n.message}</div>}
                    <div className="mt-1 text-[11px] text-neutral-500">
                      {n.entity?.kind === "leilao" && <>Leilão #{n.entity.id} · </>}
                      {timeAgo(n.createdAt)} atrás
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
